import { useCallback, useRef, useState } from 'react';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { CloudUpload, FileUp } from '@carboledger/ui/icons';
import styles from './FileDropzone.module.scss';

/**
 * Props:
 *   fileCount     current number of selected files
 *   maxFiles      disables the upload CTA once fileCount reaches this
 *   accept        native <input accept> value
 *   title         prompt heading
 *   subtitle      prompt description
 *   uploadLabel   the upload button's label
 *   helperText    caption below the upload button
 *   onFilesAdded  (File[]) => void
 *   children      rendered below the prompt — typically the selected-file grid
 */
export const FileDropzone = ({
  fileCount,
  maxFiles,
  accept,
  title,
  subtitle,
  uploadLabel,
  helperText,
  onFilesAdded,
  children,
}) => {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const isLimitReached = fileCount >= maxFiles;

  // Both sources are live FileLists; snapshot before the caller's updater reads them.
  const handleFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList ?? []);
      if (files.length === 0) return;
      onFilesAdded(files);
    },
    [onFilesAdded],
  );

  const handleInputChange = useCallback(
    (event) => {
      handleFiles(event.target.files);
      event.target.value = ''; // allow re-selecting the same file name later
    },
    [handleFiles],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragOver(false);
      if (isLimitReached) return;
      handleFiles(event.dataTransfer.files);
    },
    [isLimitReached, handleFiles],
  );

  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault();
      if (!isLimitReached) setIsDragOver(true);
    },
    [isLimitReached],
  );

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);
  const handleUploadClick = useCallback(() => inputRef.current?.click(), []);

  return (
    <div
      className={styles.dropzone}
      data-drag-over={isDragOver || undefined}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={styles.prompt}>
        <CloudUpload size={36} className={styles.icon} aria-hidden="true" />
        <Typography variant={TypographyVariants.bodySm} weight={TypographyWeights.semibold}>
          {title}
        </Typography>
        <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
          {subtitle}
        </Typography>
        <div className={styles.buttonGroup}>
          <Button
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            className={styles.uploadButton}
            leftIcon={<FileUp />}
            disabled={isLimitReached}
            onClick={handleUploadClick}
          >
            {uploadLabel}
          </Button>
          <Typography variant={TypographyVariants.caption} color={TypographyColors.secondary}>
            {helperText}
          </Typography>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={handleInputChange}
        />
      </div>

      {children}
    </div>
  );
};
